import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Button, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { faArrowLeft, faCoins, faDownload, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toSafeInteger } from 'lodash';
import { useDebounce } from 'use-debounce';
import { useHistory, useLocation } from 'react-router-dom';

import {
  ANNUALIZED_MFR_GMV_BRANDS,
  ANNUALIZED_MFR_GMV_DISTRIBUTOR_PRODUCTS,
  ANNUALIZED_MFR_GMV_DISTRIBUTORS_BY_GMV,
  ANNUALIZED_MFR_GMV_MANUFACTURERS,
  ANNUALIZED_MFR_GMV_MANUFACTURERS_EXPORT,
  getURLWithSearchParams,
} from 'lib/networking/endpoints';
import {
  backgroundDarkGrey,
  backgroundGrey5,
  black,
  darkSlate,
  ghostWhite,
  grape,
  lightPurple2,
  lightSlate,
  shadowGrape,
  shadowMedium,
  shadowVeryLight,
  slateGrey,
  softPurple,
  softPurpleLight,
  warmGray,
  white,
} from 'lib/css/colors';
import BrandCard from 'components/codify_db/BrandCard';
import Container from 'components/shared/Container';
import { DEFAULT_UPB_PAGE_SIZE } from 'lib/constants';
import DistributorCard from 'components/codify_db/DistributorCard';
import DistributorProductCard from 'components/codify_db/DistributorProductCard';
import {
  DistributorProductMappingStatus,
  DistributorProductMappingTabs,
} from 'lib/enums/DistributorProductMappingStatus';
import {
  DistributorScope,
  DistributorScopeSubtitle,
  DistributorScopeTabs,
} from 'lib/enums/DistributorScope';
import { formatGMV, formatRoundedCurrency } from 'lib/utils/FormatUtils';
import FsaLoader from 'components/shared/FsaLoader';
import ManufacturerCard from 'components/codify_db/ManufacturerCard';
import PaginationBar from 'components/shared/PaginationBar';
import SearchBar from 'components/shared/SearchBar';
import { UPBViewContext } from 'components/upb_viewer/UPBViewContainer';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';
import useFileExport from 'lib/hooks/useFileExport';
import useGet from 'lib/hooks/useGet';

// Distributor-first drill hierarchy: Distributors -> Manufacturers -> Brands -> DP Products.
// ALL_PRODUCTS is an alternate view of a selected distributor (flat product list, GMV-desc),
// toggled against the Manufacturers drill at the same level.
const VIEW = {
  DISTRIBUTORS: 'Distributors',
  MANUFACTURERS: 'Manufacturers',
  BRANDS: 'Brands',
  PRODUCTS: 'Products',
  ALL_PRODUCTS: 'AllProducts',
};

const SEARCH_PLACEHOLDER = {
  [VIEW.DISTRIBUTORS]: 'Search distributors...',
  [VIEW.MANUFACTURERS]: 'Search manufacturers...',
  [VIEW.BRANDS]: 'Search brands...',
  [VIEW.PRODUCTS]: 'Search by name, gtin',
  [VIEW.ALL_PRODUCTS]: 'Search by name, gtin',
};

const EMPTY_MESSAGE = {
  [VIEW.DISTRIBUTORS]: 'No distributors found',
  [VIEW.MANUFACTURERS]: 'No manufacturers found for this distributor',
  [VIEW.BRANDS]: 'No brands found for this manufacturer',
  [VIEW.PRODUCTS]: 'No products found for this brand',
  [VIEW.ALL_PRODUCTS]: 'No products found for this distributor',
};

// The export lists EVERY manufacturer of this distributor's brands, including ones with no recent
// GMV that the cards (GMV-only) don't show. Spell that out so the longer list isn't a surprise.
const MFR_EXPORT_TOOLTIP =
  "Every manufacturer of this distributor's brands, including those without recent GMV " +
  '(more than the manufacturer cards shown here)';

// Count-label text (ALL_PRODUCTS reads as "Products" to the user).
const VIEW_LABEL = {
  [VIEW.DISTRIBUTORS]: 'Distributors',
  [VIEW.MANUFACTURERS]: 'Manufacturers',
  [VIEW.BRANDS]: 'Brands',
  [VIEW.PRODUCTS]: 'Products',
  [VIEW.ALL_PRODUCTS]: 'Products',
};

function CodifyDBDistributorView() {
  const hasUserPermissions = useUserPermissions();
  const history = useHistory();
  const location = useLocation();
  const { viewGMV } = useContext(UPBViewContext);

  const searchParams = new URLSearchParams(location.search);
  const distributorId = searchParams.get('distributor_id');
  const manufacturerId = searchParams.get('manufacturer_id');
  const brandId = searchParams.get('brand_id');

  // Separate page states per level so the back button restores the prior position.
  const [distributorsPage, setDistributorsPage] = useState(1);
  const [manufacturersPage, setManufacturersPage] = useState(1);
  const [brandsPage, setBrandsPage] = useState(1);
  const [productsPage, setProductsPage] = useState(1);
  const [allProductsPage, setAllProductsPage] = useState(1);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery] = useDebounce(searchQuery, 600);

  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);

  // Top-level scope toggle: All / Partners (IS_DP_PARTNER) / Influence (INFLUENCED_ENABLED).
  // Defaults to Partners — the distributor viewer is a partner-oriented surface.
  const [distributorScope, setDistributorScope] = useState(DistributorScope.PARTNERS);
  // Exclude toggles — default ON, so packer brands and unbranded products are left out of the
  // headline/list on landing. The header breakdown still shows those (scope-scoped) amounts.
  const [excludePacker, setExcludePacker] = useState(true);
  const [excludeUnbranded, setExcludeUnbranded] = useState(true);
  const [excludeUnstructured, setExcludeUnstructured] = useState(true);
  // Distributor-level toggle: By Manufacturer (drill) vs All Products (flat, GMV-desc).
  const [allProductsMode, setAllProductsMode] = useState(false);
  // All Products sub-tabs: all | mapped | unmapped (mapped = linked to an unarchived mfr product).
  const [mappingStatus, setMappingStatus] = useState(DistributorProductMappingStatus.ALL);

  const viewMode = useMemo(() => {
    if (distributorId && manufacturerId && brandId) return VIEW.PRODUCTS;
    if (distributorId && manufacturerId) return VIEW.BRANDS;
    if (distributorId) return VIEW.MANUFACTURERS;
    return VIEW.DISTRIBUTORS;
  }, [distributorId, manufacturerId, brandId]);

  // The effective view: at the distributor's Manufacturers level, the All Products toggle swaps in
  // the flat product list; everywhere else it equals viewMode.
  const activeView = useMemo(
    () => (viewMode === VIEW.MANUFACTURERS && allProductsMode ? VIEW.ALL_PRODUCTS : viewMode),
    [viewMode, allProductsMode],
  );

  const updateUrlParams = useCallback(
    params => {
      const newSearchParams = new URLSearchParams(location.search);
      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          newSearchParams.set(key, value);
        } else {
          newSearchParams.delete(key);
        }
      });
      const newSearch = newSearchParams.toString();
      history.push(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`);
    },
    [location.pathname, location.search, history],
  );

  const currentPage = useMemo(() => {
    switch (activeView) {
      case VIEW.DISTRIBUTORS:
        return distributorsPage;
      case VIEW.MANUFACTURERS:
        return manufacturersPage;
      case VIEW.BRANDS:
        return brandsPage;
      case VIEW.PRODUCTS:
        return productsPage;
      case VIEW.ALL_PRODUCTS:
        return allProductsPage;
      default:
        return 1;
    }
  }, [activeView, distributorsPage, manufacturersPage, brandsPage, productsPage, allProductsPage]);

  const page = useMemo(() => Math.max(toSafeInteger(currentPage) - 1, 0), [currentPage]);
  const offset = useMemo(() => page * DEFAULT_UPB_PAGE_SIZE || 0, [page]);

  const setCurrentPage = useCallback(
    newPage => {
      switch (activeView) {
        case VIEW.DISTRIBUTORS:
          setDistributorsPage(newPage);
          break;
        case VIEW.MANUFACTURERS:
          setManufacturersPage(newPage);
          break;
        case VIEW.BRANDS:
          setBrandsPage(newPage);
          break;
        case VIEW.PRODUCTS:
          setProductsPage(newPage);
          break;
        case VIEW.ALL_PRODUCTS:
          setAllProductsPage(newPage);
          break;
      }
    },
    [activeView],
  );

  // Reset the current level's page when the search query changes.
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  // Level 1 - distributors ordered by GMV. total_gmv is the scope-aware headline (sum across all
  // matching distributors, honouring search + the partner toggle).
  const {
    data: {
      distributors = [],
      count: distributorsCount = 0,
      total_gmv: distributorsTotalGMV = 0,
      packer_gmv: packerGmv = 0,
      unbranded_gmv: unbrandedGmv = 0,
      unstructured_gmv: unstructuredGmv = 0,
    } = {},
    loading: distributorsLoading,
  } = useGet(ANNUALIZED_MFR_GMV_DISTRIBUTORS_BY_GMV, {
    limit: DEFAULT_UPB_PAGE_SIZE,
    offset: viewMode === VIEW.DISTRIBUTORS ? offset : 0,
    query: viewMode === VIEW.DISTRIBUTORS ? debouncedQuery : '',
    distributor_scope: distributorScope,
    exclude_packer: excludePacker,
    exclude_unbranded: excludeUnbranded,
    exclude_unstructured: excludeUnstructured,
  });

  // Level 2 - manufacturers carried by the selected distributor.
  const {
    data: { manufacturers = [], count: manufacturersCount = 0 } = {},
    loading: manufacturersLoading,
  } = useGet(
    ANNUALIZED_MFR_GMV_MANUFACTURERS,
    {
      distributor_id: distributorId || '',
      limit: DEFAULT_UPB_PAGE_SIZE,
      offset: activeView === VIEW.MANUFACTURERS ? offset : 0,
      query: activeView === VIEW.MANUFACTURERS ? debouncedQuery : '',
      exclude_packer: excludePacker,
      exclude_unbranded: excludeUnbranded,
      exclude_unstructured: excludeUnstructured,
    },
    !distributorId || (activeView !== VIEW.MANUFACTURERS && activeView !== VIEW.BRANDS),
  );

  // All Products view - the distributor's full DP-product catalog, flat and GMV-desc.
  const {
    data: { data: distributorProducts = [], count: distributorProductsCount = 0 } = {},
    loading: distributorProductsLoading,
  } = useGet(
    ANNUALIZED_MFR_GMV_DISTRIBUTOR_PRODUCTS,
    {
      distributor_id: distributorId || '',
      mapping_status: mappingStatus === DistributorProductMappingStatus.ALL ? '' : mappingStatus,
      limit: DEFAULT_UPB_PAGE_SIZE,
      offset: activeView === VIEW.ALL_PRODUCTS ? offset : 0,
      search_query: activeView === VIEW.ALL_PRODUCTS ? debouncedQuery : '',
      exclude_packer: excludePacker,
      exclude_unbranded: excludeUnbranded,
      exclude_unstructured: excludeUnstructured,
    },
    activeView !== VIEW.ALL_PRODUCTS || !distributorId,
  );

  // Level 3 - brands of the selected manufacturer within the distributor.
  const { data: { brands = [], count: brandsCount = 0 } = {}, loading: brandsLoading } = useGet(
    ANNUALIZED_MFR_GMV_BRANDS,
    {
      manufacturer_id: manufacturerId || '',
      distributor_id: distributorId || '',
      limit: DEFAULT_UPB_PAGE_SIZE,
      offset: viewMode === VIEW.BRANDS ? offset : 0,
      search: viewMode === VIEW.BRANDS ? debouncedQuery : '',
      exclude_packer: excludePacker,
      exclude_unbranded: excludeUnbranded,
      exclude_unstructured: excludeUnstructured,
    },
    !manufacturerId || (viewMode !== VIEW.BRANDS && viewMode !== VIEW.PRODUCTS),
  );

  // Level 4 - the distributor's DP (vendor) products for the selected brand, GMV-desc.
  const { data: { data: products = [], count: productsCount = 0 } = {}, loading: productsLoading } =
    useGet(
      ANNUALIZED_MFR_GMV_DISTRIBUTOR_PRODUCTS,
      {
        distributor_id: distributorId || '',
        brand_id: brandId || '',
        limit: DEFAULT_UPB_PAGE_SIZE,
        offset: activeView === VIEW.PRODUCTS ? offset : 0,
        search_query: activeView === VIEW.PRODUCTS ? debouncedQuery : '',
        exclude_packer: excludePacker,
        exclude_unbranded: excludeUnbranded,
        exclude_unstructured: excludeUnstructured,
      },
      activeView !== VIEW.PRODUCTS || !brandId,
    );

  // Capture the selected entity objects so the breadcrumb and header can render without a
  // separate lookup request.
  useEffect(() => {
    if (distributorId && distributors.length) {
      const match = distributors.find(d => d.id.toString() === distributorId);
      if (match) setSelectedDistributor(match);
    } else if (!distributorId) {
      setSelectedDistributor(null);
    }
  }, [distributorId, distributors]);

  useEffect(() => {
    if (manufacturerId && manufacturers.length) {
      const match = manufacturers.find(m => m.id.toString() === manufacturerId);
      if (match) setSelectedManufacturer(match);
    } else if (!manufacturerId) {
      setSelectedManufacturer(null);
    }
  }, [manufacturerId, manufacturers]);

  useEffect(() => {
    if (brandId && brands.length) {
      const match = brands.find(b => b.id.toString() === brandId);
      if (match) setSelectedBrand(match);
    } else if (!brandId) {
      setSelectedBrand(null);
    }
  }, [brandId, brands]);

  const handleDistributorClick = distributor => {
    setManufacturersPage(1);
    setAllProductsPage(1);
    setAllProductsMode(false); // always land on the Manufacturers view for a new distributor
    setMappingStatus(DistributorProductMappingStatus.ALL);
    updateUrlParams({
      distributor_id: distributor.id,
      manufacturer_id: null,
      brand_id: null,
    });
    setSearchQuery('');
  };

  const handleManufacturerClick = manufacturer => {
    setBrandsPage(1);
    updateUrlParams({ manufacturer_id: manufacturer.id, brand_id: null });
    setSearchQuery('');
  };

  const handleBrandClick = brand => {
    setProductsPage(1);
    updateUrlParams({ brand_id: brand.id });
    setSearchQuery('');
  };

  const handleBackClick = () => {
    if (viewMode === VIEW.PRODUCTS) {
      updateUrlParams({ brand_id: null });
    } else if (viewMode === VIEW.BRANDS) {
      updateUrlParams({ manufacturer_id: null });
    } else if (viewMode === VIEW.MANUFACTURERS) {
      setAllProductsMode(false); // reset the view toggle when leaving the distributor
      updateUrlParams({ distributor_id: null });
    }
    setSearchQuery('');
  };

  const onSearchQueryChange = query => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleScopeChange = scope => {
    if (scope === distributorScope) return;
    setDistributorScope(scope);
    setDistributorsPage(1);
  };

  // Excludes apply at every drill level, so reset both the distributor list and the active level's
  // page — the row counts and GMV ordering shift when a category is toggled on/off.
  const handleExcludePackerToggle = () => {
    setExcludePacker(prev => !prev);
    setDistributorsPage(1);
    setCurrentPage(1);
  };

  const handleExcludeUnbrandedToggle = () => {
    setExcludeUnbranded(prev => !prev);
    setDistributorsPage(1);
    setCurrentPage(1);
  };

  const handleExcludeUnstructuredToggle = () => {
    setExcludeUnstructured(prev => !prev);
    setDistributorsPage(1);
    setCurrentPage(1);
  };

  const handleProductsViewToggle = showAllProducts => {
    if (showAllProducts === allProductsMode) return;
    setAllProductsMode(showAllProducts);
    setManufacturersPage(1);
    setAllProductsPage(1);
    setMappingStatus(DistributorProductMappingStatus.ALL);
    setSearchQuery('');
  };

  const handleMappingTabChange = status => {
    if (status === mappingStatus) return;
    setMappingStatus(status);
    setAllProductsPage(1);
  };

  // Export the distributor's per-manufacturer breakdown. Passes the current exclude toggles so the
  // export's manufacturer set + GMV match the cards. Only offered on the Manufacturers view.
  const { exporting, onExport: handleExportManufacturers } = useFileExport(
    distributorId
      ? getURLWithSearchParams(ANNUALIZED_MFR_GMV_MANUFACTURERS_EXPORT, {
          distributor_id: distributorId,
          exclude_packer: excludePacker,
          exclude_unbranded: excludeUnbranded,
          exclude_unstructured: excludeUnstructured,
        })
      : null,
    'distributor_mfrs.xlsx',
    'Could not export manufacturers. Please try again.',
  );

  const isSearchPending = searchQuery !== debouncedQuery;
  const loading =
    distributorsLoading ||
    manufacturersLoading ||
    brandsLoading ||
    productsLoading ||
    distributorProductsLoading ||
    isSearchPending;

  const currentItemCount = useMemo(() => {
    switch (activeView) {
      case VIEW.DISTRIBUTORS:
        return distributorsCount;
      case VIEW.MANUFACTURERS:
        return manufacturersCount;
      case VIEW.BRANDS:
        return brandsCount;
      case VIEW.PRODUCTS:
        return productsCount;
      case VIEW.ALL_PRODUCTS:
        return distributorProductsCount;
      default:
        return 0;
    }
  }, [
    activeView,
    distributorsCount,
    manufacturersCount,
    brandsCount,
    productsCount,
    distributorProductsCount,
  ]);

  const totalPages = useMemo(
    () => Math.ceil(currentItemCount / DEFAULT_UPB_PAGE_SIZE),
    [currentItemCount],
  );

  // Header GMV reflects the current drill context; deeper levels reuse the distributor-scoped
  // GMV already present on the selected card.
  const headerGMV = useMemo(() => {
    switch (viewMode) {
      case VIEW.PRODUCTS:
        return selectedBrand?.annualized_monitizable_gmv || 0;
      case VIEW.BRANDS:
        return selectedManufacturer?.annualized_monitizable_gmv || 0;
      case VIEW.MANUFACTURERS:
        return selectedDistributor?.annualized_monitizable_gmv || 0;
      default:
        return distributorsTotalGMV || 0;
    }
  }, [viewMode, selectedBrand, selectedManufacturer, selectedDistributor, distributorsTotalGMV]);

  const headerSubtitle = useMemo(() => {
    switch (viewMode) {
      case VIEW.PRODUCTS:
        return selectedBrand?.name || 'Brand';
      case VIEW.BRANDS:
        return selectedManufacturer?.name || 'Manufacturer';
      case VIEW.MANUFACTURERS:
        return selectedDistributor?.name || 'Distributor';
      default:
        return DistributorScopeSubtitle[distributorScope] || 'All Distributors';
    }
  }, [viewMode, selectedBrand, selectedManufacturer, selectedDistributor, distributorScope]);

  const renderBreadcrumb = () => {
    if (viewMode === VIEW.DISTRIBUTORS) return null;
    return (
      <div className={classNames(css(styles.breadcrumb), 'mb-4')}>
        <Button variant="link" className={css(styles.backButton)} onClick={handleBackClick}>
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back
        </Button>
        <div className={css(styles.breadcrumbText)}>
          {selectedDistributor && <strong>{selectedDistributor.name}</strong>}
          {selectedManufacturer && (
            <span>
              {' '}
              / <strong>{selectedManufacturer.name}</strong>
            </span>
          )}
          {selectedBrand && (
            <span>
              {' '}
              / <strong>{selectedBrand.name}</strong>
            </span>
          )}
        </div>
        {activeView === VIEW.MANUFACTURERS &&
          hasUserPermissions([UserPermission.EXPORT_PRODUCT_DATA]) && (
            <div className={css(styles.exportWrap)}>
              <Button
                variant="outline-primary"
                size="sm"
                className="text-nowrap"
                disabled={exporting}
                onClick={handleExportManufacturers}
              >
                <FontAwesomeIcon icon={faDownload} className="mr-1" />
                {exporting ? 'Exporting…' : 'Export All Manufacturers'}
              </Button>
              <OverlayTrigger
                placement="top"
                transition={false}
                overlay={props => (
                  <Tooltip {...props} id="mfr-export-tip">
                    {MFR_EXPORT_TOOLTIP}
                  </Tooltip>
                )}
              >
                <span
                  className={css(styles.exportInfo)}
                  tabIndex={0}
                  role="button"
                  aria-label={MFR_EXPORT_TOOLTIP}
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                </span>
              </OverlayTrigger>
            </div>
          )}
      </div>
    );
  };

  const renderGrid = () => {
    if (activeView === VIEW.DISTRIBUTORS) {
      return distributors.length ? (
        distributors.map(distributor => (
          <DistributorCard
            key={distributor.id}
            distributor={distributor}
            onDistributorClick={handleDistributorClick}
          />
        ))
      ) : (
        <div className={css(styles.emptyState)}>{EMPTY_MESSAGE[VIEW.DISTRIBUTORS]}</div>
      );
    }

    if (activeView === VIEW.MANUFACTURERS) {
      return manufacturers.length ? (
        manufacturers.map(manufacturer => (
          <ManufacturerCard
            key={manufacturer.id}
            manufacturer={manufacturer}
            onManufacturerClick={handleManufacturerClick}
          />
        ))
      ) : (
        <div className={css(styles.emptyState)}>{EMPTY_MESSAGE[VIEW.MANUFACTURERS]}</div>
      );
    }

    if (activeView === VIEW.BRANDS) {
      return brands.length ? (
        brands.map(brand => (
          <BrandCard key={brand.id} brand={brand} onBrandClick={handleBrandClick} />
        ))
      ) : (
        <div className={css(styles.emptyState)}>{EMPTY_MESSAGE[VIEW.BRANDS]}</div>
      );
    }

    if (activeView === VIEW.ALL_PRODUCTS) {
      return distributorProducts.length ? (
        distributorProducts.map(product => (
          <DistributorProductCard key={product.id} product={product} />
        ))
      ) : (
        <div className={css(styles.emptyState)}>{EMPTY_MESSAGE[VIEW.ALL_PRODUCTS]}</div>
      );
    }

    return products.length ? (
      products.map(product => <DistributorProductCard key={product.id} product={product} />)
    ) : (
      <div className={css(styles.emptyState)}>{EMPTY_MESSAGE[VIEW.PRODUCTS]}</div>
    );
  };

  return (
    <Container className={css(styles.container)}>
      <Row className={classNames('mb-1 ml-0', css(styles.titleContainer))}>
        <Col sm="3">
          <div className="pl-2">
            <h4 className="font-weight-bold mb-0">UPB Distributor Viewer</h4>
            {viewMode === VIEW.DISTRIBUTORS && (
              <div className={css(styles.toggleWrap)}>
                {DistributorScopeTabs.map(scope => (
                  <Button
                    key={scope.value}
                    variant="link"
                    className={css(
                      styles.toggleSeg,
                      distributorScope === scope.value && styles.toggleSegActive,
                    )}
                    onClick={() => handleScopeChange(scope.value)}
                  >
                    {scope.label}
                  </Button>
                ))}
              </div>
            )}
            {viewMode === VIEW.MANUFACTURERS && (
              <div className={css(styles.toggleWrap)}>
                <Button
                  variant="link"
                  className={css(styles.toggleSeg, !allProductsMode && styles.toggleSegActive)}
                  onClick={() => handleProductsViewToggle(false)}
                >
                  Manufacturers
                </Button>
                <Button
                  variant="link"
                  className={css(styles.toggleSeg, allProductsMode && styles.toggleSegActive)}
                  onClick={() => handleProductsViewToggle(true)}
                >
                  All Products
                </Button>
              </div>
            )}
          </div>
        </Col>
        <Col sm="5" className={css(styles.headerGMVContainer)}>
          {viewGMV && (
            <div className={css(styles.gmvCard)}>
              <div className={css(styles.gmvHeaderRow)}>
                <div className={css(styles.gmvIconContainer)}>
                  <div className={css(styles.gmvIcon)}>
                    <FontAwesomeIcon icon={faCoins} className={css(styles.gmvIconGlyph)} />
                  </div>
                </div>
                <div className={css(styles.gmvTitleContainer)}>
                  <h5 className={css(styles.gmvTitle)}>Total GMV</h5>
                  <div className={css(styles.gmvSubtitle)}>{headerSubtitle}</div>
                </div>
                <div className={css(styles.gmvValueContainer)}>
                  {distributorsLoading && viewMode === VIEW.DISTRIBUTORS ? (
                    <FsaLoader size={28} />
                  ) : headerGMV ? (
                    <div className={css(styles.gmvValue)}>{formatRoundedCurrency(headerGMV)}</div>
                  ) : (
                    <div className={css(styles.gmvNoData)}>No data available</div>
                  )}
                </div>
              </div>
              {/* The exclude toggles carry through every drill level so the figures below stay
                  consistent; the per-category dollar breakdown is only meaningful at the
                  distributor list (it is the list-wide, scope-scoped total). */}
              <div className={css(styles.gmvBreakdown)}>
                {viewMode === VIEW.DISTRIBUTORS && (
                  <div className={css(styles.breakdownValues)}>
                    <span className={css(styles.breakdownItem)}>
                      <span className={css(styles.breakdownLabel)}>Packer:</span>{' '}
                      {formatGMV(packerGmv)}
                    </span>
                    <span className={css(styles.breakdownItem)}>
                      <span className={css(styles.breakdownLabel)}>Unstructured:</span>{' '}
                      {formatGMV(unstructuredGmv)}
                    </span>
                    <span className={css(styles.breakdownItem)}>
                      <span className={css(styles.breakdownLabel)}>Unbranded:</span>{' '}
                      {formatGMV(unbrandedGmv)}
                    </span>
                  </div>
                )}
                <div className={css(styles.excludeToggles)}>
                  <label className={css(styles.excludeLabel)}>
                    <input
                      type="checkbox"
                      checked={excludePacker}
                      onChange={handleExcludePackerToggle}
                      className={css(styles.excludeCheckbox)}
                    />
                    Exclude packer
                  </label>
                  <label className={css(styles.excludeLabel)}>
                    <input
                      type="checkbox"
                      checked={excludeUnbranded}
                      onChange={handleExcludeUnbrandedToggle}
                      className={css(styles.excludeCheckbox)}
                    />
                    Exclude unbranded
                  </label>
                  <label className={css(styles.excludeLabel)}>
                    <input
                      type="checkbox"
                      checked={excludeUnstructured}
                      onChange={handleExcludeUnstructuredToggle}
                      className={css(styles.excludeCheckbox)}
                    />
                    Exclude unstructured
                  </label>
                </div>
              </div>
            </div>
          )}
        </Col>
        <Col sm="4">
          <div className="d-flex justify-content-end flex-row align-items-center pr-2">
            <div className="w-50 text-right mr-2 text-muted d-flex justify-content-end">
              {loading ? (
                <FsaLoader size={20} />
              ) : currentItemCount > 0 ? (
                <span>
                  {`${offset + 1}-${Math.min(
                    offset + DEFAULT_UPB_PAGE_SIZE,
                    currentItemCount,
                  )} of ${currentItemCount} `}
                  {VIEW_LABEL[activeView]}
                </span>
              ) : null}
            </div>
            <div className={css(styles.searchBarWrapper)}>
              <SearchBar
                value={searchQuery}
                setValue={onSearchQueryChange}
                placeholder={SEARCH_PLACEHOLDER[activeView]}
              />
            </div>
          </div>
        </Col>
      </Row>
      <Row className={css(styles.catalogContainer)}>
        <Col md={12} className={classNames(css(styles.productSection), 'mt-3')}>
          {loading ? (
            <FsaLoader fullView size={96} label="Loading…" />
          ) : (
            <div className="d-flex justify-content-center flex-column align-items-center w-100">
              <div className={css(styles.catalogWrapper)}>
                {renderBreadcrumb()}
                {activeView === VIEW.ALL_PRODUCTS && (
                  <div className={css(styles.tabBar)}>
                    {DistributorProductMappingTabs.map(tab => (
                      <Button
                        key={tab.value}
                        variant="link"
                        className={css(styles.tab, mappingStatus === tab.value && styles.tabActive)}
                        onClick={() => handleMappingTabChange(tab.value)}
                      >
                        {tab.label}
                      </Button>
                    ))}
                  </div>
                )}
                <div className={css(styles.grid)}>{renderGrid()}</div>
              </div>
              {totalPages > 1 && (
                <div className="my-4">
                  <PaginationBar
                    totalPages={totalPages}
                    currentPageNumber={page}
                    onPageChange={newPage => setCurrentPage(newPage + 1)}
                  />
                </div>
              )}
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  titleContainer: {
    top: 50,
    backgroundColor: backgroundDarkGrey,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0px 40px 0px 20px',
    minHeight: '110px',
  },
  catalogContainer: {
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 150px)',
  },
  productSection: {
    background: backgroundGrey5,
    position: 'sticky',
    top: 50,
    display: 'flex',
  },
  headerGMVContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '8px',
  },
  toggleWrap: {
    display: 'inline-flex',
    gap: 2,
    marginTop: '8px',
    padding: 3,
    backgroundColor: ghostWhite,
    border: `1px solid ${lightPurple2}`,
    borderRadius: '10px',
  },
  exportWrap: {
    marginLeft: 'auto',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  exportInfo: {
    cursor: 'help',
    display: 'inline-flex',
    alignItems: 'center',
    color: warmGray,
    fontSize: '13px',
  },
  toggleSeg: {
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    padding: '4px 14px',
    borderRadius: '7px',
    fontSize: '12px',
    fontWeight: 600,
    color: warmGray,
    textDecoration: 'none',
    transition: 'all 0.15s ease',
    ':hover': {
      color: softPurple,
      textDecoration: 'none',
    },
    ':focus': {
      outline: 'none',
      boxShadow: 'none',
      textDecoration: 'none',
    },
  },
  toggleSegActive: {
    backgroundColor: softPurple,
    color: white,
    cursor: 'default',
    ':hover': {
      color: white,
    },
  },
  gmvCard: {
    background: `linear-gradient(135deg, ${ghostWhite} 0%, ${softPurpleLight} 100%)`,
    borderRadius: '16px',
    border: `1px solid ${softPurpleLight}`,
    boxShadow: `0 4px 12px ${shadowVeryLight}, 0 2px 8px ${shadowGrape}, inset 0 1px 0 ${white}`,
    padding: '14px 24px',
    minWidth: '400px',
    maxWidth: '600px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.2s ease-in-out',
    ':hover': {
      transform: 'translateY(-1px)',
    },
  },
  gmvHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  gmvBreakdown: {
    marginTop: '10px',
    paddingTop: '10px',
    borderTop: `1px solid ${softPurpleLight}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  breakdownValues: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px 18px',
    fontSize: '12px',
    fontWeight: 700,
    color: softPurple,
  },
  breakdownItem: {
    whiteSpace: 'nowrap',
  },
  breakdownLabel: {
    color: warmGray,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    fontSize: '11px',
  },
  excludeToggles: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px 16px',
  },
  excludeLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    margin: 0,
    fontSize: '11px',
    fontWeight: 600,
    color: warmGray,
    cursor: 'pointer',
  },
  excludeCheckbox: {
    cursor: 'pointer',
    accentColor: softPurple,
  },
  gmvIconContainer: {
    marginRight: '12px',
  },
  gmvIcon: {
    width: '36px',
    height: '36px',
    backgroundColor: softPurple,
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
  },
  gmvIconGlyph: {
    color: white,
    fontSize: '16px',
  },
  gmvTitleContainer: {
    flex: 1,
  },
  gmvTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '700',
    color: softPurple,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  gmvSubtitle: {
    fontSize: '12px',
    color: warmGray,
    fontWeight: '500',
    marginTop: '2px',
    opacity: 0.8,
  },
  gmvValueContainer: {
    textAlign: 'center',
  },
  gmvValue: {
    fontSize: '28px',
    fontWeight: '800',
    color: softPurple,
    letterSpacing: '-0.5px',
  },
  gmvSpinner: {
    color: softPurple,
  },
  gmvNoData: {
    fontSize: '16px',
    color: warmGray,
    fontStyle: 'italic',
    opacity: 0.7,
  },
  searchBarWrapper: {
    maxWidth: '320px',
    minWidth: '280px',
  },
  catalogWrapper: {
    width: '100%',
    padding: '0 20px',
  },
  tabBar: {
    display: 'flex',
    gap: '4px',
    borderBottom: `1px solid ${lightPurple2}`,
    marginBottom: '8px',
  },
  tab: {
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    padding: '8px 18px',
    fontSize: '14px',
    fontWeight: 600,
    color: warmGray,
    textDecoration: 'none',
    borderBottom: '3px solid transparent',
    borderRadius: 0,
    marginBottom: '-1px',
    transition: 'all 0.15s ease',
    ':focus': {
      outline: 'none',
      boxShadow: 'none',
      textDecoration: 'none',
    },
    ':hover': {
      color: softPurple,
      textDecoration: 'none',
    },
  },
  tabActive: {
    color: softPurple,
    borderBottom: `3px solid ${softPurple}`,
    cursor: 'default',
  },
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'flex-start',
    minHeight: 500,
    gap: '8px',
    padding: '20px 0',
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: ghostWhite,
    padding: '12px 20px',
    borderRadius: '10px',
    border: `1px solid ${lightSlate}`,
    boxShadow: `0 1px 3px ${shadowMedium}`,
  },
  backButton: {
    padding: '0',
    marginRight: '15px',
    textDecoration: 'none',
    color: grape,
    fontSize: '16px',
    fontWeight: 500,
    transition: 'color 0.2s ease',
    ':hover': {
      textDecoration: 'none',
      color: black,
    },
  },
  breadcrumbText: {
    fontSize: '16px',
    color: darkSlate,
    fontWeight: 500,
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: slateGrey,
    fontSize: '18px',
    fontWeight: 500,
    width: '100%',
  },
});

export default CodifyDBDistributorView;
